import DeleteIcon from '@mui/icons-material/Delete';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import {
  Alert,
  Checkbox,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import DashboardLayout from '../../Components/Dashboard/DashboardLayout/DashboardLayout';
import Footer from '../../Components/Footer/Footer';
import Slideshow from '../../Components/Slideshow/Slideshow';
import { useFacility } from '../../context/FacilityContext';
import {
  deleteImagesFunction,
  listAssignedResidentsFunction,
  loadImagesFunction,
} from '../../firebase/functions';
import './StaffGallery.css';

function StaffGallery() {
  const [images, setImages] = useState([]);
  const [isSlideshowOpen, setIsSlideshowOpen] = useState(false);
  const { facilityId } = useFacility();

  const [errorAlert, setErrorAlert] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const [assignedResidents, setAssignedResidents] = useState([]);
  const [selectedResidentId, setSelectedResidentId] = useState('');

  useEffect(() => {
    const initialize = () => {
      setIsSlideshowOpen(false);
      setImages([]);
      setAssignedResidents([]);
      loadResidents();
      loadImages(selectedResidentId);
    };
    if (facilityId) initialize();
  }, [facilityId, selectedResidentId]);

  const loadImages = (selectedResident) => {
    setIsLoading(true);
    loadImagesFunction({
      facilityId: facilityId,
      residentId: selectedResident,
    })
      .then((result) => {
        const imgs = result.data.images.map((x) => ({ ...x, selected: false }));
        setImages(imgs.filter((x) => !x.type));
      })
      .catch((error) => {
        setImages([]);
        setErrorAlert(error?.message);
      })
      .finally(() => setIsLoading(false));
  };

  const loadResidents = () => {
    listAssignedResidentsFunction({
      facilityId: facilityId,
    }).then((result) => {
      setAssignedResidents(result.data.residents);
    });
  };

  const deleteSelectedImages = () => {
    const imageUrls = selectedImages.map((img) => img.imageUrl);

    if (
      !window.confirm(
        `Are you sure you want to delete the ${imageUrls.length} images you have selected below? This action is irreversible!`,
      )
    ) {
      return;
    }

    if (selectedImages.length === 0) {
      setErrorAlert('No images selected for deletion!');
      return;
    }
    setIsLoading(true);

    deleteImagesFunction({
      imageUrls: imageUrls,
      deleteFromFirestore: true,
    })
      .then((response) => {
        setImages(images.filter((img) => !imageUrls.includes(img.imageUrl)));
      })
      .catch((error) => {
        setErrorAlert(error.message || 'Failed to delete images');
      })
      .finally(() => setIsLoading(false));
  };

  const handleSelectImage = (index) => {
    setImages(
      images.map((img, idx) =>
        idx === index ? { ...img, selected: !img.selected } : img,
      ),
    );
  };

  const toggleSlideShow = () => {
    setIsSlideshowOpen(!isSlideshowOpen);
  };

  const selectedImages = images.filter((img) => img.selected);
  const imagesToShow = selectedImages.length > 0 ? selectedImages : images;

  return (
    <div>
      <DashboardLayout facilityId={facilityId} isLoading={isLoading}>
        <div>
          {errorAlert && (
            <Alert
              severity="error"
              onClose={() => {
                setErrorAlert('');
              }}
            >
              {errorAlert}
            </Alert>
          )}
        </div>

        <div className="actionsNavbar">
          <div className="actions">
            <FormControl sx={{ minWidth: '200px' }}>
              <InputLabel id="selectLabel" shrink={true}>
                Change To Resident
              </InputLabel>
              <Select
                value={
                  assignedResidents?.some((x) => x.id == selectedResidentId)
                    ? selectedResidentId
                    : ''
                }
                onChange={(e) => setSelectedResidentId(e.target.value)}
                displayEmpty
                inputProps={{ 'aria-label': 'Resident selector' }}
                sx={{ height: '46px' }}
                labelId="selectLabel"
                label=" Change To Resident"
              >
                <MenuItem key="-" value="">
                  Recently Uploaded
                </MenuItem>
                {assignedResidents &&
                  assignedResidents.map((resident) => (
                    <MenuItem key={resident.id} value={resident.id}>
                      {resident.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <IconButton
              aria-label="view as slideshow"
              className="actionButton"
              onClick={toggleSlideShow}
            >
              {isSlideshowOpen ? (
                <div>
                  <PhotoLibraryIcon /> Back To Gallery
                </div>
              ) : (
                <div>
                  <SlideshowIcon /> Slideshow
                </div>
              )}
            </IconButton>

            {selectedImages.length > 0 && !isSlideshowOpen && (
              <IconButton
                className="actionButton"
                aria-label="delete images"
                onClick={deleteSelectedImages}
              >
                <DeleteIcon />
                Delete
              </IconButton>
            )}
          </div>

          <span
            className="imageCount"
            style={{
              color: selectedImages.length > 0 ? '#000000' : '#5a5a5a',
            }}
          >
            {selectedImages.length > 1
              ? `${selectedImages.length} images selected`
              : `${selectedImages.length} image selected`}
          </span>
        </div>

        <hr />
        {!isSlideshowOpen && (
          <ImageList cols={3} variant="masonry" id="galleryContainer">
            {images &&
              Array.from(images).map((img, index) => (
                <ImageListItem key={index}>
                  <img
                    src={img.imageUrl}
                    srcSet={img.imageUrl}
                    alt={img.residentId}
                    loading="lazy"
                    style={{
                      width: '100%',
                      height: '100%',
                      transition: 'opacity 0.4s ease',
                      opacity: img.selected ? 0.8 : 1,
                    }}
                  />
                  <Checkbox
                    checked={img.selected}
                    onChange={() => handleSelectImage(index)}
                    className="selectImageCheckbox"
                  />
                  <ImageListItemBar
                    position="below"
                    title={
                      assignedResidents.find((x) => x.id === img.residentId)
                        ?.name
                    }
                    subtitle={img.comment}
                  />
                </ImageListItem>
              ))}
          </ImageList>
        )}

        {isSlideshowOpen && (
          <Slideshow images={imagesToShow} facilityId={facilityId} />
        )}
      </DashboardLayout>
      <Footer />
    </div>
  );
}

StaffGallery.propTypes = {
  facilityId: PropTypes.string,
};

export default StaffGallery;
